import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  fetchTycoonState,
  hireStaff,
  upgradeStaff,
  claimPassive,
  openBranch,
  type TycoonState,
} from "../api/tycoon.api";
import { fetchBalance } from "../api/auth.api";
import { BalanceTicker } from "../components/BalanceTicker";
import { OfficeFloor } from "../components/OfficeFloor";

const STAFF_LABELS: Record<string, string> = {
  CASHIER: "Cashier",
  TRADER: "Trader",
  QUANT: "Quant",
  MANAGER: "Manager",
  MARKETING: "Marketing",
  SECURITY: "Security",
};

const STAFF_TAGLINES: Record<string, string> = {
  CASHIER: "Earns tokens",
  TRADER: "Earns more",
  QUANT: "High earner",
  MANAGER: "+% to branch",
  MARKETING: "+brand/hr",
  SECURITY: "+supply",
};

/** City labels are now server-driven via state.cities[].name — these are
 * just fallbacks for branches whose city code isn't in the catalog (shouldn't
 * happen unless an old branch survives a city retirement). */
const CITY_LABEL_FALLBACK: Record<string, string> = {
  TBILISI: "Tbilisi",
  BATUMI: "Batumi",
  YEREVAN: "Yerevan",
  ISTANBUL: "Istanbul",
};

const REGION_LABELS: Record<string, string> = {
  CAUCASUS: "Caucasus",
  EUROPE: "Europe",
  ASIA: "Asia",
  AMERICAS: "Americas",
  MIDDLE_EAST: "Middle East",
};

const REGION_ORDER = ["CAUCASUS", "EUROPE", "ASIA", "AMERICAS", "MIDDLE_EAST"] as const;

/** Compact thousand/million/billion formatter for big city prices ("125M"
 * reads better than "125,000,000" on a small button). */
function formatCompact(n: bigint | number): string {
  const v = typeof n === "bigint" ? Number(n) : n;
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(v >= 10_000_000_000 ? 0 : 1)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
}

/** Base level-1 hire cost per staff type. Mirrors STAFF_BASE.hireCostTokens
 * in apps/api/.../game.config.ts. Keep in sync. */
const STAFF_HIRE_COST: Record<string, number> = {
  CASHIER: 100,
  TRADER: 1_000,
  QUANT: 10_000,
  MANAGER: 5_000,
  MARKETING: 3_000,
  SECURITY: 2_000,
};

/** Per-level upgrade cost multiplier and ceiling. Mirrors
 * STAFF_LEVEL_COST_MULTIPLIER and STAFF_MAX_LEVEL in game.config.ts. */
const STAFF_LEVEL_COST_MULTIPLIER = 2.0;
const STAFF_MAX_LEVEL = 20;

/** Token cost to upgrade ONE staff member from currentLevel → currentLevel+1.
 * Matches staffUpgradeCost(type, currentLevel, currentLevel+1) on the server. */
function nextUpgradeCost(type: string, currentLevel: number): number {
  const base = STAFF_HIRE_COST[type] ?? 0;
  return Math.round(base * Math.pow(STAFF_LEVEL_COST_MULTIPLIER, currentLevel));
}

const HIRE_TYPES = ["CASHIER", "TRADER", "QUANT", "MANAGER", "MARKETING", "SECURITY"] as const;

export function TycoonScene() {
  const [state, setState] = useState<TycoonState | null>(null);
  const [balance, setBalance] = useState<bigint>(0n);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([fetchTycoonState(), fetchBalance()]);
      setState(s);
      setBalance(BigInt(b.balance));
      setError(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "Failed to load");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleClaim = async () => {
    setBusy(true);
    try {
      await claimPassive();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleHire = async (branchId: string, type: string) => {
    setBusy(true);
    setError(null);
    try {
      await hireStaff({ branchId, type });
      await refresh();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "hire failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUpgrade = async (staffId: string, currentLevel: number) => {
    setBusy(true);
    setError(null);
    try {
      await upgradeStaff({ staffId, targetLevel: currentLevel + 1 });
      await refresh();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "upgrade failed");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenBranch = async (city: string) => {
    setBusy(true);
    setError(null);
    try {
      await openBranch(city);
      await refresh();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setError(msg ?? "open branch failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="loading">Loading your exchange…</div>;

  // Look up display name for an owned branch via the server's catalog.
  const cityNameFor = (code: string) =>
    state.cities.find((c) => c.code === code)?.name ?? CITY_LABEL_FALLBACK[code] ?? code;
  const cityFlagFor = (code: string) =>
    state.cities.find((c) => c.code === code)?.flag ?? "";

  return (
    <div className="tycoon-scene">
      <BalanceTicker balance={balance} tycoonState={state} onClaim={handleClaim} />

      {error && (
        <motion.div
          className="error-banner"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.div>
      )}

      <section>
        <h2>Branches</h2>
        <div className="branches-grid">
          {state.branches.map((branch) => {
            const branchStaff = state.staff.filter((s) => s.branchId === branch.id);
            return (
              <motion.div
                key={branch.id}
                className="branch-card"
                whileTap={{ scale: 0.995 }}
              >
                <div className="branch-header">
                  <h3>
                    {cityFlagFor(branch.city) && (
                      <span className="branch-flag" aria-hidden>
                        {cityFlagFor(branch.city)}
                      </span>
                    )}
                    {cityNameFor(branch.city)}
                  </h3>
                  <span className="branch-meta">
                    L{branch.level} · {branchStaff.length} staff
                  </span>
                </div>

                <OfficeFloor
                  branch={branch}
                  staff={branchStaff}
                  busy={busy}
                  onHire={(branchId, role) => handleHire(branchId, role)}
                  onUpgrade={(staffId, currentLevel) =>
                    handleUpgrade(staffId, currentLevel)
                  }
                />

                {branch.managerBonusPct > 0 && (
                  <div className="branch-bonus" title="Manager bonus active in this branch">
                    👔 Manager boost +{Math.round(branch.managerBonusPct * 100)}%
                  </div>
                )}

                <div className="staff-actions hire-grid">
                  {HIRE_TYPES.map((t) => {
                    const count = branchStaff.filter((s) => s.type === t).length;
                    const cap = state.staffCaps[t] ?? Infinity;
                    const atCap = count >= cap;
                    const hireCost = STAFF_HIRE_COST[t] ?? 0;
                    const canAfford = balance >= BigInt(hireCost);
                    const disabled = busy || atCap || !canAfford;
                    const titleHint = atCap
                      ? `${STAFF_LABELS[t]} cap (${cap}) reached for this branch`
                      : !canAfford
                        ? `Need ${hireCost.toLocaleString()} tokens (you have ${balance.toLocaleString()})`
                        : `${STAFF_TAGLINES[t]} · ${hireCost.toLocaleString()} tokens`;
                    return (
                      <button
                        key={t}
                        className={`hire-btn type-${t.toLowerCase()} ${atCap ? "at-cap" : ""} ${
                          !canAfford && !atCap ? "cant-afford" : ""
                        }`}
                        onClick={() => handleHire(branch.id, t)}
                        disabled={disabled}
                        type="button"
                        title={titleHint}
                      >
                        <span className="hire-icon" aria-hidden>
                          {atCap ? "✓" : "+"}
                        </span>
                        <span>{STAFF_LABELS[t]}</span>
                        <span className="hire-tagline">
                          {count}/{cap}
                          {atCap ? " · full" : ` · ${hireCost.toLocaleString()}🪙`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {branchStaff.length > 0 && (
                  <ul className="staff-list">
                    {branchStaff.map((s) => {
                      const atMaxLevel = s.level >= STAFF_MAX_LEVEL;
                      const upgradeCost = atMaxLevel ? 0 : nextUpgradeCost(s.type, s.level);
                      const canAffordUpgrade = !atMaxLevel && balance >= BigInt(upgradeCost);
                      const upgradeTitle = atMaxLevel
                        ? `Already at max level (${STAFF_MAX_LEVEL})`
                        : !canAffordUpgrade
                          ? `Need ${upgradeCost.toLocaleString()} tokens (you have ${balance.toLocaleString()})`
                          : `Upgrade to Lv ${s.level + 1} for ${upgradeCost.toLocaleString()} tokens`;
                      return (
                        <li key={s.id} className={`staff-row role-${s.type.toLowerCase()}`}>
                          <div>
                            <strong>{STAFF_LABELS[s.type] ?? s.type}</strong>
                            <span className="muted"> · Lv {s.level}</span>
                          </div>
                          <span className="rate">
                            {Number(s.hourlyTokenRate) > 0
                              ? `${Number(s.hourlyTokenRate).toLocaleString()}/hr`
                              : STAFF_TAGLINES[s.type] ?? "support"}
                          </span>
                          <button
                            onClick={() => handleUpgrade(s.id, s.level)}
                            disabled={busy || atMaxLevel || !canAffordUpgrade}
                            type="button"
                            title={upgradeTitle}
                            className={!canAffordUpgrade && !atMaxLevel ? "cant-afford" : ""}
                          >
                            {atMaxLevel
                              ? "Max"
                              : `Lv ${s.level + 1} · ${upgradeCost.toLocaleString()}🪙`}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      <BranchPicker
        cities={state.cities}
        balance={balance}
        liveBrand={Number(state.exchange.brandAwareness)}
        totalBranches={state.exchange.totalBranches}
        busy={busy}
        onOpenBranch={handleOpenBranch}
      />
    </div>
  );
}

// ─── Branch picker (regional tabs + city cards) ─────────────────────────

interface BranchPickerProps {
  cities: TycoonState["cities"];
  balance: bigint;
  liveBrand: number;
  totalBranches: number;
  busy: boolean;
  onOpenBranch: (city: string) => void;
}

function BranchPicker({
  cities,
  balance,
  liveBrand,
  totalBranches,
  busy,
  onOpenBranch,
}: BranchPickerProps) {
  // Cities to show in the picker = everything not yet owned. Locked ones
  // stay visible (greyed out + unlock hint) so players can see what's
  // ahead and chase a target.
  const candidates = cities.filter((c) => !c.isOwned);
  if (candidates.length === 0) return null;

  // Group by region in display order; skip empty regions.
  const byRegion = REGION_ORDER.map((region) => ({
    region,
    cities: candidates.filter((c) => c.region === region),
    ownedCount: cities.filter((c) => c.region === region && c.isOwned).length,
    totalCount: cities.filter((c) => c.region === region).length,
  })).filter((g) => g.cities.length > 0);

  // Default to the first region with at least one *unlocked* (affordable
  // by branch-count gate) city — that's where the player has live options.
  const defaultRegion =
    byRegion.find((g) => g.cities.some((c) => c.isUnlocked))?.region ?? byRegion[0].region;
  const [activeRegion, setActiveRegion] = useState<string>(defaultRegion);

  const activeGroup = byRegion.find((g) => g.region === activeRegion) ?? byRegion[0];

  return (
    <section className="expand-section">
      <h2>Expand</h2>
      <p className="expand-hint">
        New cities cost <strong>tokens</strong> + <strong>brand awareness</strong> — hire
        Marketing staff to grow brand. Higher-tier cities unlock as you open more branches.
      </p>

      {/* Region tabs — horizontal scroll on narrow viewports. */}
      <div className="region-tabs" role="tablist">
        {byRegion.map((g) => {
          const active = g.region === activeRegion;
          return (
            <button
              key={g.region}
              role="tab"
              aria-selected={active}
              className={`region-tab ${active ? "active" : ""}`}
              onClick={() => setActiveRegion(g.region)}
              type="button"
            >
              <span className="region-tab-name">{REGION_LABELS[g.region]}</span>
              <span className="region-tab-meta">
                {g.ownedCount}/{g.totalCount}
              </span>
            </button>
          );
        })}
      </div>

      <div className="city-grid">
        {[...activeGroup.cities]
          // Available first, then locked — within each band, cheapest first
          // so the next achievable target is at the top.
          .sort((a, b) => {
            if (a.isUnlocked !== b.isUnlocked) return a.isUnlocked ? -1 : 1;
            const ta = BigInt(a.openCostTokens);
            const tb = BigInt(b.openCostTokens);
            return ta < tb ? -1 : ta > tb ? 1 : 0;
          })
          .map((city) => (
            <CityCard
              key={city.code}
              city={city}
              balance={balance}
              liveBrand={liveBrand}
              totalBranches={totalBranches}
              busy={busy}
              onOpen={() => onOpenBranch(city.code)}
            />
          ))}
      </div>
    </section>
  );
}

interface CityCardProps {
  city: TycoonState["cities"][number];
  balance: bigint;
  liveBrand: number;
  totalBranches: number;
  busy: boolean;
  onOpen: () => void;
}

function CityCard({ city, balance, liveBrand, totalBranches, busy, onOpen }: CityCardProps) {
  const tokenCost = BigInt(city.openCostTokens);
  const brandCost = Number(city.openCostBrandAwareness);
  const hasTokens = balance >= tokenCost;
  const hasBrand = liveBrand >= brandCost;
  const branchesShort = Math.max(0, city.unlockBranchCount - totalBranches);
  const canAfford = city.isUnlocked && hasTokens && hasBrand;

  let status: "available" | "locked" | "need-resources";
  let statusLabel: string;
  if (!city.isUnlocked) {
    status = "locked";
    statusLabel = `Need ${branchesShort} more branch${branchesShort === 1 ? "" : "es"}`;
  } else if (!canAfford) {
    status = "need-resources";
    if (!hasTokens && !hasBrand)
      statusLabel = `Need ${formatCompact(tokenCost)}🪙 + ${formatCompact(brandCost)}📣`;
    else if (!hasTokens) statusLabel = `Need ${formatCompact(tokenCost - balance)}🪙 more`;
    else statusLabel = `Need ${formatCompact(brandCost - liveBrand)}📣 more`;
  } else {
    status = "available";
    statusLabel = "Tap to open";
  }

  return (
    <motion.button
      className={`city-card status-${status}`}
      onClick={canAfford && !busy ? onOpen : undefined}
      disabled={busy || !canAfford}
      type="button"
      whileTap={canAfford ? { scale: 0.97 } : undefined}
      title={
        canAfford
          ? `Open a branch in ${city.name}`
          : `${city.name} — ${statusLabel}`
      }
    >
      <div className="city-card-header">
        <span className="city-flag" aria-hidden>
          {city.flag}
        </span>
        <span className="city-name">{city.name}</span>
      </div>

      <div className="city-card-cost">
        {tokenCost > 0n && (
          <span className={`cost-chip ${hasTokens ? "" : "missing"}`}>
            🪙 {formatCompact(tokenCost)}
          </span>
        )}
        {brandCost > 0 && (
          <span className={`cost-chip ${hasBrand ? "" : "missing"}`}>
            📣 {formatCompact(brandCost)}
          </span>
        )}
        {tokenCost === 0n && brandCost === 0 && (
          <span className="cost-chip free">Free</span>
        )}
      </div>

      <div className="city-card-status">{statusLabel}</div>
    </motion.button>
  );
}
